import { Button } from "@/components/ui/button/button";
import FloatingInput from "@/components/ui/floatingInput";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "@/components/ui/form/form";
import Typography from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import useChangePassword from "@/features/register/hooks/useChangePassword";
import { PasswordSchema } from "@/features/register/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import PasswordWithRequirements from "@/components/ui/passwordRequirements";
import { isPasswordValid } from "@/lib/utils";

const changePasswordSchema = z
	.object({
		currentPassword: z.string().min(1, "Current password is required"),
		newPassword: PasswordSchema,
		confirmPassword: z.string(),
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});
type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export default function ChangePasswordPage() {
	const { user } = useAuth();
	const changePasswordMutation = useChangePassword();
	const { toast } = useToast();
	const navigate = useNavigate();

	const form = useForm<ChangePasswordFormData>({
		resolver: zodResolver(changePasswordSchema),
		defaultValues: {
			currentPassword: "",
			newPassword: "",
			confirmPassword: "",
		},
	});
	const password = form.watch("newPassword");
	const onSubmit = (data: ChangePasswordFormData) => {
		changePasswordMutation.mutate(
			{
				guestId: user.id,
				payload: {
					current_password: data.currentPassword,
					new_password: data.newPassword,
				},
			},
			{
				onSuccess: () => {
					toast({
						title: "Success",
						description: "Password has been changed successfully",
					});
					form.reset();
				},
				onError: (error) => {
					toast({
						title: "Error",
						description: error.message,
						variant: "destructive",
					});
				},
			},
		);
	};

	const handleForgotPassword = () => {
		navigate({
			to: "/password-recovery",
		});
	};

	return (
		<div className="space-y-6">
			<Typography variant="h5">Change Password</Typography>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
					<FormField
						control={form.control}
						name="currentPassword"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<FloatingInput
										type="password"
										label="Current Password"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="newPassword"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<PasswordWithRequirements
										label="New Password"
										value={field.value ?? ""}
										onChange={field.onChange}
										error={form.formState.errors.newPassword}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="confirmPassword"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<FloatingInput
										type="password"
										label="Confirm New Password"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<Button
						size={"sm"}
						variant={"link"}
						type="button"
						onClick={handleForgotPassword}
					>
						Forgot password?
					</Button>
					<Button
						type="submit"
						className="w-full"
						disabled={!isPasswordValid(password)}
					>
						Submit
					</Button>
				</form>
			</Form>
		</div>
	);
}
